import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Clock, ArrowRight, ShieldCheck, HourglassMedium, SpeakerHigh, Megaphone, CaretDown, X as XIcon } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { NewsletterInline } from './NewsletterPrompts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// (Msg 724) Country → City expandable picker for the Landing "Sort by your area"
// dropdown. Lists Canada / USA / Mexico with the visitor's IP-derived country
// ordered FIRST, then the remaining two in canonical order. Cities sorted
// alphabetically inside each country. Each city row shows the accumulated
// `event_days` count so fans see the true number of events available.
const CountryCityPicker = ({ options, userCountry, value, onChange, open, setOpen, expanded, setExpanded }) => {
  const wrapRef = useRef(null);
  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, setOpen]);

  // Group options by country and order them so the visitor's country lands first.
  const COUNTRY_ORDER_DEFAULT = ['Canada', 'USA', 'Mexico'];
  const grouped = {};
  for (const c of options) {
    const co = (c.country || 'Other').trim() || 'Other';
    if (!grouped[co]) grouped[co] = [];
    grouped[co].push(c);
  }
  Object.values(grouped).forEach((arr) => arr.sort((a, b) => a.city.localeCompare(b.city)));
  const allCountries = Object.keys(grouped);
  const orderedCountries = [];
  if (userCountry && grouped[userCountry]) orderedCountries.push(userCountry);
  for (const co of COUNTRY_ORDER_DEFAULT) {
    if (co !== userCountry && grouped[co]) orderedCountries.push(co);
  }
  // Any "Other" leftovers (rare; e.g. unknown country) appended last
  for (const co of allCountries) {
    if (!orderedCountries.includes(co)) orderedCountries.push(co);
  }

  const buttonLabel = value
    ? value
    : 'Sort by your area';

  const toggleCountry = (co) => setExpanded((prev) => ({ ...prev, [co]: !prev[co] }));

  return (
    <div className="text-center mb-4" ref={wrapRef}>
      <div className="relative inline-block" data-testid="city-sort-wrap">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-2 bg-white border-2 border-gray-200 rounded-full px-4 py-2 shadow-sm hover:border-[#FF6B35] transition-colors"
          data-testid="city-sort-button"
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <MapPin size={16} weight="bold" className="text-[#FF6B35]" />
          <span className="text-sm font-bold text-gray-800">{buttonLabel}</span>
          {value ? (
            <XIcon
              size={14}
              weight="bold"
              className="text-gray-500 hover:text-[#FF6B35]"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              data-testid="city-sort-clear"
            />
          ) : (
            <CaretDown size={14} weight="bold" className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          )}
        </button>
        {open && (
          <div
            className="absolute left-1/2 -translate-x-1/2 mt-2 w-[280px] sm:w-[320px] bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden"
            data-testid="city-sort-panel"
            role="listbox"
          >
            <div className="max-h-[60vh] overflow-y-auto py-2">
              {/* Clear/All option */}
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                className={`w-full text-left px-4 py-2 text-sm font-bold hover:bg-gray-50 ${!value ? 'text-[#FF6B35]' : 'text-gray-700'}`}
                data-testid="city-sort-all"
              >
                All cities · sort by my area
              </button>
              <div className="border-t border-gray-100 my-1" />
              {orderedCountries.map((co) => {
                const isOpen = expanded[co] !== false; // default expanded
                const totalDays = grouped[co].reduce((sum, c) => sum + (c.event_days || c.count || 0), 0);
                return (
                  <div key={co} data-testid={`city-sort-country-${co.toLowerCase()}`}>
                    <button
                      type="button"
                      onClick={() => toggleCountry(co)}
                      className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 text-left"
                    >
                      <span className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-gray-900">
                        {co === userCountry && <span className="text-[10px] bg-[#FF6B35] text-white px-1.5 py-0.5 rounded-full">Nearest</span>}
                        {co}
                        <span className="text-[11px] font-bold text-gray-500">({totalDays} events/day)</span>
                      </span>
                      <CaretDown size={12} weight="bold" className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="pb-1">
                        {grouped[co].map((c) => (
                          <button
                            key={`${c.city}-${c.country}`}
                            type="button"
                            onClick={() => { onChange(c.city); setOpen(false); }}
                            className={`w-full flex items-center justify-between px-6 py-2 text-sm hover:bg-orange-50 text-left ${value === c.city ? 'bg-orange-50 text-[#FF6B35] font-bold' : 'text-gray-700'}`}
                            data-testid={`city-sort-option-${c.city.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <span>{c.city}</span>
                            <span className="text-[11px] font-bold text-gray-500">{c.event_days || c.count || 0}/day</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const EventListingsPreview = () => {
  const [listings, setListings] = useState([]);
  const [allListings, setAllListings] = useState([]); // full unsliced list for city-filter override
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bumperAds, setBumperAds] = useState([]);
  const [spotlightAds, setSpotlightAds] = useState([]);
  // (Msg 787) Rotate through ALL active campaigns instead of freezing on
  // the first one. Backend already filters by flight-date + geo + shuffles;
  // frontend cycles the returned list every 8s so every advertiser gets
  // airtime and the admin-portal /advertise preview never looks single-banner.
  const [bumperIdx, setBumperIdx] = useState(0);
  const [spotlightIdx, setSpotlightIdx] = useState(0);
  useEffect(() => {
    if (bumperAds.length < 2) return;
    const t = setInterval(() => setBumperIdx(i => (i + 1) % bumperAds.length), 8000);
    return () => clearInterval(t);
  }, [bumperAds.length]);
  useEffect(() => {
    if (spotlightAds.length < 2) return;
    const t = setInterval(() => setSpotlightIdx(i => (i + 1) % spotlightAds.length), 8000);
    return () => clearInterval(t);
  }, [spotlightAds.length]);
  const [geoScope, setGeoScope] = useState(null); // { label, type: 'city' | 'country' | 'all' }
  const [cityOptions, setCityOptions] = useState([]); // [{city, country, count, event_days}]
  const [cityFilter, setCityFilter] = useState(''); // user-selected city override
  const [userCountry, setUserCountry] = useState(''); // IP-derived country for nearest-first ordering
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expandedCountries, setExpandedCountries] = useState({}); // { Canada: true, USA: false, ... }
  const scrollTracked = useRef(false);

  useEffect(() => {
    fetchListings();
    fetchStats();
    fetchBumperAds();
    fetchSpotlightAds();
    axios.get(`${API}/venues/cities-with-counts?min_count=3`)
      .then(r => setCityOptions((r.data?.items || []).filter(c => c && c.city)))
      .catch(() => {});
  }, []);

  // Track scroll into Upcoming Events: +1 x fan cards / 3
  useEffect(() => {
    if (scrollTracked.current || !listings.length) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !scrollTracked.current) {
        scrollTracked.current = true;
        const boost = Math.max(1, Math.round(listings.length / 3));
        axios.post(`${API}/stats/engage`, { event: 'scroll_events', increment: boost }).catch(() => {});
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    const el = document.querySelector('[data-testid="event-listings-section"]');
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [listings]);

  // Pull the user's IP geo once (cached in localStorage for the session).
  const getUserGeo = async () => {
    try {
      const cached = sessionStorage.getItem('wsp_user_geo');
      if (cached) return JSON.parse(cached);
      const r = await axios.get('https://ipapi.co/json/', { timeout: 3500 });
      const geo = {
        city: (r.data?.city || '').trim(),
        region: (r.data?.region || '').trim(),
        country: (r.data?.country_name || r.data?.country || '').trim(),
      };
      sessionStorage.setItem('wsp_user_geo', JSON.stringify(geo));
      return geo;
    } catch {
      return { city: '', region: '', country: '' };
    }
  };

  const fetchListings = async () => {
    try {
      const [geo, response] = await Promise.all([
        getUserGeo(),
        axios.get(`${API}/venues/listings?status=published`),
      ]);
      // Persist visitor country for the Sort-by-area Country→City dropdown
      // (nearest-first ordering). Normalize to canonical {Canada, USA, Mexico}.
      const _co = (geo.country || '').toLowerCase();
      if (_co.includes('canada')) setUserCountry('Canada');
      else if (_co.includes('mexico')) setUserCountry('Mexico');
      else if (_co.includes('united states') || _co === 'usa' || _co === 'us') setUserCountry('USA');
      else setUserCountry('');
      const all = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      setAllListings(all);
      const normalize = (s) => (s || '').toString().trim().toLowerCase();
      const isFifa = (l) => !!l.fifa_license;

      // Step 1 — Pick the 3 best FIFA Fan Festivals, ONE per host country
      // (Canada / USA / Mexico). Order: nearest country first, then the
      // remaining two by distance. Never two from the same city.
      const _now = Date.now();
      const parseTs = (l) => {
        if (!l?.event_date) return Number.POSITIVE_INFINITY;
        const t = Date.parse(`${l.event_date}${l.event_time ? ' ' + l.event_time : ''}`);
        return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
      };
      const haversineKm = (a, b) => {
        if (a.lat == null || b.lat == null) return null;
        const R = 6371; const toRad = (d) => d * Math.PI / 180;
        const dLat = toRad(b.lat - a.lat); const dLng = toRad(b.lng - a.lng);
        const x = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng/2)**2;
        return 2 * R * Math.asin(Math.sqrt(x));
      };
      const userPt = (typeof geo.latitude === 'number' && typeof geo.longitude === 'number')
        ? { lat: geo.latitude, lng: geo.longitude } : null;
      const fifaPool = all.filter(l => !!l.fifa_license);
      // Group by country, pick the BEST candidate per country (nearest if
      // we have geo, else earliest upcoming).
      const byCountry = new Map();
      for (const l of fifaPool) {
        const c = (l.country || '').trim();
        if (!c) continue;
        const ts = parseTs(l);
        const future = ts >= _now ? 0 : 1;
        const km = userPt && typeof l.latitude === 'number' && typeof l.longitude === 'number'
          ? haversineKm(userPt, { lat: l.latitude, lng: l.longitude }) : null;
        const shares = l.share_count || 0;
        const scored = { l, future, km, ts, shares };
        const cur = byCountry.get(c);
        if (!cur) { byCountry.set(c, scored); continue; }
        // Prefer future-first, then nearer if known, then earlier, then more-shared.
        if (cur.future !== scored.future) { if (scored.future < cur.future) byCountry.set(c, scored); continue; }
        if (cur.km != null && scored.km != null) {
          if (scored.km < cur.km) byCountry.set(c, scored);
          continue;
        }
        if (cur.km == null && scored.km != null) { byCountry.set(c, scored); continue; }
        if (cur.km != null && scored.km == null) continue;
        if (scored.ts < cur.ts) byCountry.set(c, scored);
      }
      // Order the 3 country reps by user distance (nearest first). When no
      // geo, fall back to: visitor's country first if known, else Canada,
      // then USA, then Mexico.
      const reps = Array.from(byCountry.values()).slice();
      reps.sort((a, b) => {
        if (a.km != null && b.km != null) return a.km - b.km;
        if (a.km != null) return -1;
        if (b.km != null) return 1;
        // Fallback order based on visitor country (or default Canada→USA→Mexico)
        const preferred = (geo.country || '').toLowerCase();
        const order = ['canada', 'usa', 'united states', 'mexico'];
        if (preferred && order.includes(preferred)) {
          const ac = (a.l.country || '').toLowerCase();
          const bc = (b.l.country || '').toLowerCase();
          if (ac === preferred && bc !== preferred) return -1;
          if (bc === preferred && ac !== preferred) return 1;
        }
        return a.ts - b.ts;
      });
      // Take 1-per-country (up to 3). Backfill from the FIFA pool if a
      // country is missing data so the row never blanks.
      const fifaTop3 = reps.slice(0, 3).map(s => s.l);
      if (fifaTop3.length < 3) {
        const seen = new Set(fifaTop3.map(l => l.id));
        const usedCities = new Set(fifaTop3.map(l => (l.city || '').toLowerCase()));
        for (const l of fifaPool) {
          if (fifaTop3.length >= 3) break;
          if (seen.has(l.id)) continue;
          if (usedCities.has((l.city || '').toLowerCase())) continue; // never repeat a city
          fifaTop3.push(l);
          seen.add(l.id);
          usedCities.add((l.city || '').toLowerCase());
        }
      }
      const fifaPinnedIds = new Set(fifaTop3.map(l => l.id));

      // Step 2 — Build the LOCAL feed: same geo cascade as before but with
      // FIFA events EXCLUDED so the landing page doesn't repeat them and
      // also doesn't stack FIFA events on top of the user's own city.
      let scope = { type: 'all', label: '' };
      let filtered = [];
      const localPool = all.filter(l => !isFifa(l) && !fifaPinnedIds.has(l.id));

      if (geo.city) {
        filtered = localPool.filter(l => normalize(l.city) === normalize(geo.city));
        if (filtered.length) scope = { type: 'city', label: geo.city };
      }
      if (!filtered.length && geo.region) {
        filtered = localPool.filter(l => normalize(l.province) === normalize(geo.region));
        if (filtered.length) scope = { type: 'region', label: geo.region };
      }
      if (!filtered.length && geo.country) {
        filtered = localPool.filter(l => normalize(l.country) === normalize(geo.country));
        if (filtered.length) scope = { type: 'country', label: geo.country };
      }
      if (!filtered.length) {
        filtered = localPool;
        scope = { type: 'all', label: '' };
      }

      // Sort the local feed by soonest event date so the next 3 (or 6 when
      // there are <3 FIFA picks) are time-relevant.
      filtered.sort((a, b) => parseTs(a) - parseTs(b));

      // Dedup multi-day / match-coverage fan-out cards (backend emits one
      // card per day or per country with a shared parent_listing_id). The
      // listings grid only needs ONE entry per real event.
      const seenKeys = new Set();
      const eventKey = (l) => {
        if (l.parent_listing_id) return `pid::${l.parent_listing_id}`;
        if (l.id) return `id::${l.id}`;
        return `n::${normalize(l.event_name)}::${normalize(l.venue_name)}::${normalize(l.city)}`;
      };
      const filteredDedup = filtered.filter(l => {
        const k = eventKey(l);
        if (seenKeys.has(k)) return false;
        seenKeys.add(k);
        return true;
      });
      const fifaSeen = new Set(fifaTop3.map(eventKey));
      const localUnique = filteredDedup.filter(l => !fifaSeen.has(eventKey(l)));

      setGeoScope(scope);
      // Final composition — at most 6 cards: 3 FIFA pins + up to 3 local rows.
      // If there are fewer than 3 FIFA events, backfill from the local feed.
      const localSlice = localUnique.slice(0, Math.max(3, 6 - fifaTop3.length));
      setListings([...fifaTop3, ...localSlice].slice(0, 6));
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/venues/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchBumperAds = async () => {
    try {
      // 1) Try live paid ads from /ads/serve (geo + schedule aware)
      const live = await axios.get(`${API}/ads/serve?slot=bumper&limit=5`).then(r => r.data?.items || []).catch(() => []);
      if (live.length) {
        setBumperAds(live);
        live.forEach(ad => {
          if (ad.ad_purchase_id) {
            const idx = Number.isFinite(ad.image_index) ? ad.image_index : -1;
            axios.post(`${API}/ads/purchase/${ad.ad_purchase_id}/impression${idx >= 0 ? `?image_index=${idx}` : ''}`).catch(() => {});
          }
        });
        return;
      }
      // 2) Fall back to legacy seeded /ads/bumper (rich card) so the slot is
      // never blank during low-inventory weeks.
      const response = await axios.get(`${API}/ads/bumper`);
      const items = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      setBumperAds(items);
      items.forEach(ad => {
        if (ad.id) axios.post(`${API}/ads/${ad.id}/impression`).catch(() => {});
      });
    } catch {}
  };

  // Featured Spotlight is shown in the in-feed ad slot on tablet/desktop
  // (≥md). Mobile users see the Bumper Ad instead — bumper inventory is
  // priced per-device, so this preserves the tier promise.
  const fetchSpotlightAds = async () => {
    try {
      const live = await axios.get(`${API}/ads/serve?slot=spotlight&limit=3`).then(r => r.data?.items || []).catch(() => []);
      if (live.length) {
        setSpotlightAds(live);
        live.forEach(ad => {
          if (ad.ad_purchase_id) {
            const idx = Number.isFinite(ad.image_index) ? ad.image_index : -1;
            axios.post(`${API}/ads/purchase/${ad.ad_purchase_id}/impression${idx >= 0 ? `?image_index=${idx}` : ''}`).catch(() => {});
          }
        });
        return;
      }
      const seeded = await axios.get(`${API}/ads/spotlight`).then(r => r.data?.items || r.data || []).catch(() => []);
      setSpotlightAds(Array.isArray(seeded) ? seeded : []);
    } catch { /* spotlight fetch is best-effort; slot falls back to mobile-only bumper */ }
  };

  return (
    <section className="py-24 sm:py-32 bg-gray-50" data-testid="event-listings-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {cityOptions.length > 0 && (
          <CountryCityPicker
            options={cityOptions}
            userCountry={userCountry}
            value={cityFilter}
            onChange={setCityFilter}
            open={dropdownOpen}
            setOpen={setDropdownOpen}
            expanded={expandedCountries}
            setExpanded={setExpandedCountries}
          />
        )}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl tracking-tight uppercase text-gray-900 mb-4">
            Upcoming Events
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {cityFilter ? (<>Live events in <span className="font-bold text-[#FF6B35]">{cityFilter}</span></>) : (<>
              {geoScope?.type === 'city' && (<>Soccer mayhem &amp; viewing parties happening near <span className="font-bold text-[#FF6B35]">{geoScope.label}</span></>)}
              {geoScope?.type === 'region' && (<>Showing the closest events in <span className="font-bold text-[#FF6B35]">{geoScope.label}</span> — none in your city yet</>)}
              {geoScope?.type === 'country' && (<>Nearest events to you in <span className="font-bold text-[#FF6B35]">{geoScope.label}</span></>)}
              {(!geoScope || geoScope?.type === 'all') && (<>Discover what's happening in your city — celebrations, festivals, soccer mayhem and viewing parties</>)}
            </>)}
          </p>
        </motion.div>

        {/* Coming Soon Counter */}
        {stats && stats.coming_soon > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12"
            data-testid="coming-soon-counter"
          >
            <div className="bg-gradient-to-r from-[#FF6B35]/10 via-[#FF8C42]/10 to-[#FF006E]/10 border-2 border-dashed border-[#FF6B35]/40 rounded-xl p-6 text-center">
              <div className="flex items-center justify-center gap-3 mb-3">
                <HourglassMedium size={28} weight="fill" className="text-[#FF6B35] animate-pulse" />
                <span className="font-['Bebas_Neue'] text-3xl sm:text-4xl text-[#FF6B35] tracking-wide">{stats.coming_soon} More Coming Soon</span>
                <HourglassMedium size={28} weight="fill" className="text-[#FF6B35] animate-pulse" />
              </div>
              <p className="text-gray-600 text-sm mb-4">{stats.published} live events + {stats.coming_soon} venues getting verified. New listings added weekly.</p>
              <Link 
                to="/venue-registration"
                className="inline-flex items-center gap-2 text-[#FF6B35] font-bold text-sm hover:underline"
                data-testid="coming-soon-register-link"
              >
                Want to be next? List your venue
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="text-center text-gray-500">Loading events...</div>
        ) : listings.length === 0 ? (
          <div className="text-center" data-testid="no-events-message">
            <p className="text-gray-500 mb-6">No events listed yet. Be the first venue to list your event!</p>
            <Link 
              to="/venue-registration" 
              className="bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white px-8 py-4 font-bold uppercase tracking-wider hover:shadow-lg transition-all inline-block rounded-lg"
              data-testid="list-venue-cta"
            >
              List Your Venue
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {(cityFilter ? allListings.filter(l => (l.city || '').toLowerCase() === cityFilter.toLowerCase()) : listings).map((listing, index) => (
                <React.Fragment key={listing.id}>
                  {/* In-feed Ad slot after every 3rd card: Bumper (800×300) on
                      mobile, Featured Spotlight on tablet/desktop. The pricing
                      tier sells "mobile-only" so this enforces it visually. */}
                  {index === 3 && (bumperAds.length > 0 || spotlightAds.length > 0) && (
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      viewport={{ once: true }}
                      className="md:col-span-2 lg:col-span-3"
                      data-testid="in-feed-ad-slot"
                    >
                      {/* Mobile (<md): Bumper Ad — full-bleed 8:3 burger banner */}
                      {bumperAds.length > 0 && (() => {
                        const ad = bumperAds[bumperIdx % bumperAds.length];
                        const isLive = !!ad.ad_purchase_id;
                        const trackClick = () => {
                          if (isLive) {
                            const idx = Number.isFinite(ad.image_index) ? ad.image_index : -1;
                            axios.post(`${API}/ads/purchase/${ad.ad_purchase_id}/click${idx >= 0 ? `?image_index=${idx}` : ''}`).catch(() => {});
                          }
                          else if (ad.id) axios.post(`${API}/ads/${ad.id}/click`).catch(() => {});
                        };
                        const dest = ad.cta_link || '#';
                        const isExternal = /^https?:\/\//i.test(dest);
                        return (
                          <div className="md:hidden" data-testid="bumper-ad">
                            {isLive && ad.banner_url ? (
                              <div className="flex flex-col items-start">
                                <div className="flex items-center gap-2 mb-1.5 px-1">
                                  <span className="inline-flex items-center text-[10px] font-black uppercase tracking-widest text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full" data-testid="bumper-ad-sponsored-label">Sponsored</span>
                                  {ad.company && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 truncate">· {ad.company}</span>
                                  )}
                                </div>
                                <div className="relative w-full aspect-[8/3]" style={{ aspectRatio: '8 / 3' }}>
                                  {isExternal ? (
                                    <a href={dest} target="_blank" rel="noopener noreferrer sponsored" onClick={trackClick} className="absolute inset-0 block rounded-xl overflow-hidden border border-gray-200 hover:border-[#FF6B35]/50 transition-colors" data-testid="bumper-ad-live">
                                      <img src={ad.banner_url} alt={ad.company || 'Sponsored'} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                    </a>
                                  ) : (
                                    <Link to={dest} onClick={trackClick} className="absolute inset-0 block rounded-xl overflow-hidden border border-gray-200 hover:border-[#FF6B35]/50 transition-colors" data-testid="bumper-ad-live">
                                      <img src={ad.banner_url} alt={ad.company || 'Sponsored'} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                    </Link>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <Link to={ad.cta_link} onClick={trackClick} className="block rounded-xl overflow-hidden border border-gray-700 hover:border-[#FF6B35]/50 transition-all group relative" data-testid="bumper-ad-house">
                                <div className="relative aspect-[8/3]">
                                  <img src="https://images.unsplash.com/photo-1619810816144-68dbc1f695e8?w=800&q=80" alt="Juicy burger and fries" className="absolute inset-0 w-full h-full object-cover" />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30" />
                                  <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 py-4">
                                    <p className="text-[10px] text-[#FF6B35] font-bold uppercase tracking-widest mb-1">Sponsored</p>
                                    <h3 className="font-['Bebas_Neue'] text-2xl uppercase text-white mb-2 leading-tight">{ad.headline}</h3>
                                    <p className="text-gray-300 text-xs mb-3 max-w-xs leading-relaxed">{ad.description}</p>
                                    <span className="inline-flex items-center gap-1 bg-[#FF6B35] text-white px-4 py-2 rounded-lg font-bold text-xs uppercase">
                                      {ad.cta_text} <ArrowRight size={12} weight="bold" />
                                    </span>
                                  </div>
                                </div>
                              </Link>
                            )}
                          </div>
                        );
                      })()}
                      {/* Tablet & Desktop (≥md): Featured Spotlight banner */}
                      {spotlightAds.length > 0 && (() => {
                        const ad = spotlightAds[spotlightIdx % spotlightAds.length];
                        const isLive = !!ad.ad_purchase_id;
                        const trackClick = () => {
                          if (isLive) {
                            const idx = Number.isFinite(ad.image_index) ? ad.image_index : -1;
                            axios.post(`${API}/ads/purchase/${ad.ad_purchase_id}/click${idx >= 0 ? `?image_index=${idx}` : ''}`).catch(() => {});
                          }
                          else if (ad.id) axios.post(`${API}/ads/${ad.id}/click`).catch(() => {});
                        };
                        const dest = ad.cta_link || '#';
                        const isExternal = /^https?:\/\//i.test(dest);
                        return (
                          <div className="hidden md:block" data-testid="in-feed-spotlight">
                            <div className="flex items-center gap-2 mb-2 px-1">
                              <span className="inline-flex items-center text-[10px] font-black uppercase tracking-widest text-[#FF6B35] bg-[#FF6B35]/10 px-2 py-1 rounded-full">Sponsored</span>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Featured Spotlight</span>
                              {ad.company && <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 truncate">· {ad.company}</span>}
                            </div>
                            {isLive && ad.banner_url ? (
                              isExternal ? (
                                <a href={dest} target="_blank" rel="noopener noreferrer sponsored" onClick={trackClick} className="block relative rounded-xl overflow-hidden border-2 border-[#FF6B35] transition-all group" data-testid="in-feed-spotlight-live">
                                  <img src={ad.banner_url} alt={ad.company || 'Sponsored'} className="w-full h-auto object-cover" loading="lazy" />
                                </a>
                              ) : (
                                <Link to={dest} onClick={trackClick} className="block relative rounded-xl overflow-hidden border-2 border-[#FF6B35] transition-all group" data-testid="in-feed-spotlight-live">
                                  <img src={ad.banner_url} alt={ad.company || 'Sponsored'} className="w-full h-auto object-cover" loading="lazy" />
                                </Link>
                              )
                            ) : (
                              <Link to={ad.cta_link || '/advertise'} onClick={trackClick} className="block relative rounded-xl overflow-hidden border-2 border-[#FF6B35] transition-all group" data-testid="in-feed-spotlight-house">
                                <div className="bg-gradient-to-r from-[#FF6B35]/5 via-white to-[#FF6B35]/5 p-6">
                                  <div className="flex items-center gap-5">
                                    <div className="w-14 h-14 bg-gradient-to-br from-[#FF6B35] to-[#FF8C42] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                                      <Megaphone size={26} weight="bold" className="text-white" />
                                    </div>
                                    <div className="flex-1">
                                      <h3 className="font-['Bebas_Neue'] text-2xl uppercase text-gray-900 mb-1">{ad.headline}</h3>
                                      <p className="text-gray-500 text-sm">{ad.description}</p>
                                    </div>
                                    <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white px-5 py-2.5 rounded-lg font-bold text-sm uppercase group-hover:shadow-lg transition-all flex-shrink-0">
                                      {ad.cta_text || 'Learn More'} <ArrowRight size={14} weight="bold" />
                                    </span>
                                  </div>
                                </div>
                              </Link>
                            )}
                          </div>
                        );
                      })()}
                    </motion.div>
                  )}
                <motion.div
                  key={listing.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100"
                  data-testid={`event-card-${index}`}
                >
                  <div className="relative">
                    {listing.image_url ? (
                      <img 
                        src={listing.image_url} 
                        alt={listing.event_name}
                        className="w-full h-48 object-cover"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-[#FF6B35] to-[#FF8C42] flex items-center justify-center">
                        <span className="text-white font-bold text-lg opacity-50">{listing.event_type}</span>
                      </div>
                    )}
                    {listing.fifa_license && (
                      <div className="absolute top-3 right-3 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
                        <ShieldCheck size={12} weight="bold" />VERIFIED
                      </div>
                    )}
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-xs px-3 py-1 rounded-full font-bold text-gray-800">
                      {listing.event_type}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-['Bebas_Neue'] text-2xl uppercase text-gray-900 mb-1">{listing.event_name}</h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{listing.event_description}</p>
                    
                    <div className="space-y-1.5 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin size={14} weight="bold" className="text-[#FF6B35]" />
                        <span>{listing.city || listing.location_address?.split(',')[1]?.trim()}, {listing.country}</span>
                      </div>
                      {listing.event_date && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar size={14} weight="bold" className="text-[#FF6B35]" />
                          <span>{listing.event_date}</span>
                        </div>
                      )}
                      {listing.event_time && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock size={14} weight="bold" className="text-[#FF6B35]" />
                          <span>{listing.event_time}</span>
                        </div>
                      )}
                    </div>

                    {listing.audio_quality === 'full_commentary' && (
                      <div className="flex items-center gap-2 text-xs font-bold text-[#06A77D] mb-3">
                        <SpeakerHigh size={14} weight="fill" />
                        <span>Live Commentary</span>
                      </div>
                    )}

                    {listing.vibe_tags && (() => {
                      // vibe_tags can arrive as either a comma-separated
                      // string (legacy seed) or a real array (newer rows).
                      const tagsArr = Array.isArray(listing.vibe_tags)
                        ? listing.vibe_tags
                        : String(listing.vibe_tags).split(',');
                      const clean = tagsArr.map(t => (t || '').toString().trim()).filter(Boolean);
                      if (!clean.length) return null;
                      return (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {clean.slice(0, 3).map(tag => (
                            <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{tag}</span>
                          ))}
                          {clean.length > 3 && (
                            <span className="text-xs text-gray-400 px-1">+{clean.length - 3}</span>
                          )}
                        </div>
                      );
                    })()}

                    <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                      <p className="text-sm font-bold text-[#FF6B35] uppercase tracking-wider">{listing.venue_name}</p>
                      {listing.entry_type && (
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                          listing.entry_type === 'free_event' || listing.entry_type === 'open_event' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {listing.entry_type === 'free_event' ? 'FREE' : 
                           listing.entry_type === 'open_event' ? 'OPEN' : 
                           listing.entry_type === 'ticketed' ? 'TICKETS' :
                           listing.entry_type === 'bottle_service' ? 'VIP' : 
                           listing.entry_type.replace('_', ' ').toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
                </React.Fragment>
              ))}
            </div>

            <div className="text-center">
              <Link 
                to="/events" 
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white px-8 py-4 font-bold uppercase tracking-wider hover:shadow-lg transition-all rounded-lg"
                data-testid="view-all-events-button"
              >
                View All Events
                <ArrowRight size={20} weight="bold" />
              </Link>
            </div>

            {/* Festival schedule newsletter hook */}
            <div className="max-w-xl mx-auto mt-10">
              <NewsletterInline
                hook="Never miss a kickoff. Get the schedule and vibe guide for every festival day sent to your inbox."
                buttonText="Get Schedule"
                testId="events-newsletter"
                source="City Fan"
                className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
};
